import { isNil } from 'lodash';
import { StyleSheet } from 'react-native';
import { MBView } from '@shared/components/MBView';
import { View } from '@shared/components/View';
import { CardBlockScrollProvider } from '@shared/components/blocks/contexts/scroll';
import { MBLoadingIndicator } from '@shared/components/loading';
import { MBTextH4 } from '@shared/components/typography';
import { MBCardDetailsOptionsButton } from '@shared/scenes/cards/details/containers/optionsButton';
import { ExternalBatchContextProvider } from '@shared/scenes/cards/entityBulkActions/BatchCreateContext';
import {
  ResponsiveFormProvider,
  useResponsiveFormContext,
} from '@shared/scenes/entity/components/ResponsiveFormContext';
import { CardEntityEditForm } from '@shared/scenes/entity/edit/components/CardEntityEditForm';
import { useEditCardEntity } from '@shared/scenes/entity/edit/hooks/useEditCardEntity';
import { CREATE_MODAL_FIXED_WIDTH, Colors, EXTERNAL_FORM_MAX_WIDTH, grid } from '@shared/styles';
import { useChannel } from '@shared/util/hooks';

export function ReadOnlyCardEntityForm({
  channelId,
  templateSlug,
  entityId,
}: {
  channelId: number;
  templateSlug: string;
  entityId: string;
}) {
  const channel = useChannel({ channelId });
  const workflowTemplateId = channel?.workflowTemplateId;
  const channelSlug = channel?.slug;
  if (isNil(channelSlug)) {
    return <MBLoadingIndicator fill />;
  }

  return (
    <CardBlockScrollProvider>
      <ExternalBatchContextProvider>
        <ResponsiveFormProvider>
          <Form
            workflowTemplateId={workflowTemplateId ?? undefined}
            templateSlug={templateSlug}
            entityId={entityId}
            channelSlug={channelSlug}
          />
        </ResponsiveFormProvider>
      </ExternalBatchContextProvider>
    </CardBlockScrollProvider>
  );
}

function Form({
  workflowTemplateId,
  templateSlug,
  entityId,
  channelSlug,
}: {
  workflowTemplateId?: string;
  templateSlug: string;
  entityId: string;
  channelSlug: string;
}) {
  const isResponsiveForms = useResponsiveFormContext();

  const { components, fields, updateField, isLoading, viewTemplate, viewTemplateTitle } =
    useEditCardEntity({
      entityId,
      workflowTemplateId,
      viewTemplateId: templateSlug,
      isReadOnly: true,
    });

  return (
    <MBView style={styles.container}>
      {isLoading && <MBLoadingIndicator fill />}
      {!isLoading && (
        <View fill>
          <MBView style={styles.header}>
            <MBView
              style={[styles.headerContent, isResponsiveForms && styles.headerContentResponsive]}
            >
              <MBTextH4 ellipsizeMode="tail" numberOfLines={1} style={{ flex: 1 }}>
                {viewTemplateTitle}
              </MBTextH4>
              <View style={{ width: grid(5) }}>
                <MBCardDetailsOptionsButton
                  channelSlug={channelSlug}
                  cardId={entityId}
                  hiddenCardOptions={[
                    'edit',
                    'duplicate',
                    'share',
                    'cardHistory',
                    'print',
                    'selectMultiple',
                    'delete',
                    'bulkUpdate',
                  ]}
                />
              </View>
            </MBView>
          </MBView>
          <MBView style={styles.form}>
            <CardEntityEditForm
              components={components}
              fields={fields}
              updateField={updateField}
              errors={[]}
              workflowTemplateId={workflowTemplateId}
              viewTemplateId={viewTemplate?.id}
              entityId={entityId}
              style={[styles.formContent, isResponsiveForms && styles.formContentResponsive]}
            />
          </MBView>
        </View>
      )}
    </MBView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: '100%',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    minHeight: '100vh',
  },
  form: {
    flex: 1,
    width: '100%',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    paddingLeft: grid(1),
    paddingRight: grid(1),
  },
  formContent: {
    width: '100%',
    maxWidth: EXTERNAL_FORM_MAX_WIDTH,
    alignSelf: 'center',
    paddingBottom: grid(8),
  },
  formContentResponsive: {
    maxWidth: CREATE_MODAL_FIXED_WIDTH,
  },
  header: {
    width: '100%',
    alignItems: 'center',
    paddingLeft: grid(1),
    paddingRight: grid(1),
  },
  headerContent: {
    width: '100%',
    maxWidth: EXTERNAL_FORM_MAX_WIDTH,
    height: 64,
    padding: grid(1),
    paddingHorizontal: grid(3),
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottomColor: Colors.v2.gray05,
    borderBottomWidth: 1,
  },
  headerContentResponsive: {
    maxWidth: CREATE_MODAL_FIXED_WIDTH,
    paddingLeft: grid(4),
    borderBottomWidth: 0,
  },
});
